import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { Workflow } from '@/components/ApprovalStageWizard/types';
import type { Employee } from '@/types/timesheet';
import { initializeMockData } from '@/data/mockData';
import * as workflowService from '@/services/workflowService';
import { getAllEmployees } from '@/services/employeeService';
import { MOCK_APPROVALS, convertMockApprovalsToWorkflows, mapBackendWorkflowToFrontend } from '../utils';

export interface UseWorkflowDataResult {
  workflows: Workflow[];
  setWorkflows: React.Dispatch<React.SetStateAction<Workflow[]>>;
  employees: Employee[];
  setEmployees: React.Dispatch<React.SetStateAction<Employee[]>>;
  isLoading: boolean;
  error: string | null;
  loadWorkflows: () => Promise<void>;
  loadEmployees: () => Promise<void>;
}

/**
 * Owns workflow/employee data loading for the approval workflows page,
 * including the debounced company-changed refetch.
 */
export const useWorkflowData = (isLive: boolean): UseWorkflowDataResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Unified workflows state (single source of truth)
  const [workflows, setWorkflows] = useState<Workflow[]>([]);

  // Employees for assignment dialog
  const [employees, setEmployees] = useState<Employee[]>([]);

  // Load workflows based on mode
  const loadWorkflows = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (isLive) {
        // Fetch from API in live mode
        const backendWorkflows = await workflowService.getAllWorkflows();
        const frontendWorkflows = backendWorkflows.map(mapBackendWorkflowToFrontend);
        setWorkflows(frontendWorkflows);
      } else {
        // Use mock data in demo mode
        setWorkflows(convertMockApprovalsToWorkflows(MOCK_APPROVALS));
      }
    } catch (err) {
      console.error('Failed to load workflows:', err);
      setError('Failed to load workflows. Please try again.');
      toast.error('Failed to load workflows');
    } finally {
      setIsLoading(false);
    }
  }, [isLive]);

  // Load employees based on mode
  const loadEmployees = useCallback(async () => {
    try {
      if (isLive) {
        const apiEmployees = await getAllEmployees();
        setEmployees(apiEmployees);
      } else {
        setEmployees(initializeMockData().employees);
      }
    } catch (err) {
      console.error('Failed to load employees:', err);
      // Fall back to mock data if API fails
      setEmployees(initializeMockData().employees);
    }
  }, [isLive]);

  // Load data on mount and when mode changes
  useEffect(() => {
    loadWorkflows();
    loadEmployees();
  }, [loadWorkflows, loadEmployees]);

  // Listen for company-changed event to refetch employees when switching companies.
  // Debounced to coalesce rapid switches and avoid stale-context 403s.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(() => {
        if (signal?.aborted) return;
        if (isLive) {
          loadEmployees();
        }
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [isLive, loadEmployees]);

  return {
    workflows,
    setWorkflows,
    employees,
    setEmployees,
    isLoading,
    error,
    loadWorkflows,
    loadEmployees,
  };
};
